import { ArrowRight, Bookmark, RotateCcw, Share2 } from "lucide-react";
import {
  facadeMaterials,
  formatPrice,
  houseModels,
  layouts,
  roofMaterials,
  steps
} from "../data";
import { calculatePrice } from "../pricing";
import { validateConfiguration } from "../rules";
import { useConfiguratorStore } from "../store";

interface SummaryPanelProps {
  onSave: () => void;
  onShare: () => void;
}

export function SummaryPanel({ onSave, onShare }: SummaryPanelProps) {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const step = useConfiguratorStore((state) => state.step);
  const setStep = useConfiguratorStore((state) => state.setStep);
  const reset = useConfiguratorStore((state) => state.reset);
  const price = calculatePrice(configuration);
  const ruleResult = validateConfiguration(configuration);
  const currentIndex = steps.findIndex((item) => item.id === step);
  const model = houseModels.find((item) => item.id === configuration.modelId);
  const layout = layouts.find((item) => item.id === configuration.layoutId);
  const monthly = Math.round(price.total * 0.8 / 360 + price.total * 0.8 * 0.035 / 12);

  const continueFlow = () => {
    const next = steps[Math.min(currentIndex + 1, steps.length - 1)];
    setStep(next.id);
  };

  return (
    <aside className="summary-panel" aria-label="Din konfiguration">
      <div className="summary-panel-top">
        <div className="summary-heading">
          <span>Din konfiguration</span>
          <button type="button" onClick={reset} title="Nulstil konfiguration">
            <RotateCcw size={15} />
          </button>
        </div>
        <div className="summary-house">
          <span className="summary-house-icon"><span /></span>
          <div>
            <strong>{model?.name}</strong>
            <small>{model?.tagline}</small>
          </div>
        </div>
        <dl className="summary-list">
          <div><dt>Størrelse</dt><dd>{configuration.areaM2} m²</dd></div>
          <div><dt>Plan</dt><dd>{layout?.name}</dd></div>
          <div><dt>Værelser</dt><dd>{configuration.bedrooms}</dd></div>
          <div><dt>Facade</dt><dd>{facadeMaterials.find((item) => item.id === configuration.facadeMaterialId)?.name}</dd></div>
          <div><dt>Tag</dt><dd>{roofMaterials.find((item) => item.id === configuration.roofMaterialId)?.name}</dd></div>
          <div><dt>Tilvalg</dt><dd>{configuration.options.length}</dd></div>
        </dl>
        {!ruleResult.valid && (
          <div className="rule-warning" role="alert">
            {ruleResult.messages.map((message) => <p key={message}>{message}</p>)}
          </div>
        )}
      </div>

      <div className="summary-panel-bottom">
        <div className="price-block" aria-live="polite">
          <span>Estimeret pris inkl. moms</span>
          <strong>{formatPrice(price.total)}</strong>
          <small>Finansiering fra ca. {formatPrice(monthly)}/md.</small>
        </div>
        <button
          className="button button-primary full-width continue-button"
          type="button"
          disabled={!ruleResult.valid}
          onClick={continueFlow}
        >
          {currentIndex === steps.length - 1 ? "Få et tilbud" : `Fortsæt til ${steps[currentIndex + 1].shortLabel.toLowerCase()}`}
          <ArrowRight size={17} />
        </button>
        <div className="secondary-actions">
          <button type="button" onClick={onSave}><Bookmark size={15} /> Gem</button>
          <button type="button" onClick={onShare}><Share2 size={15} /> Del</button>
        </div>
        <p className="price-disclaimer">
          Vejledende pris. Grundforhold, transport, myndighedskrav og endelig projektering kan påvirke prisen.
        </p>
      </div>
    </aside>
  );
}
